// Catalog.ID Client — Delegate Activation Wizard
// ============================================================================
// 7-step wizard, rebuilt on the personal shape in round 6 (registration-
// restructure §6 amendment, §13.3, M13):
//
//   Terms → Keygen (throwaway credentials, silent) → Activate (invitation
//   lookup + /api/delegate/activate) → Device setup → Email verify →
//   Phone verify → Print (issue Unlock Sheet → entropy ceremony → finalize →
//   print the Delegate Membership Kit → type-back gate → onboarding complete)
//
// What it used to do: mint the FINAL password and supersecret silently at its
// identity step, print them at "export" behind an acknowledgement checkbox, and
// stop — no ceremony, no finalize, no gate, no onboarding-completion call, and
// no way back into a crashed run. Every one of those is now the same machinery
// the personal wizard uses:
//
//  * finalize-at-print (§3): the account is activated with credentials the
//    invitee never sees; the real password, supersecret and XRPL master key are
//    minted at the print step by the entropy ceremony and installed by the
//    shared sequence in auth/finalize-credentials.ts;
//  * email + phone claims (§13.3): mandatory for every delegate now, which is
//    what lets the kit carry an Account Unlock Sheet — the original reason a
//    locked delegate could only go through an operator-reviewed unlock;
//  * a crash-resume stash (§3.3) on the SAME localStorage record the two
//    registration wizards use, so the login screen's unfinished-registration
//    banner brings an interrupted activation back;
//  * `POST /api/account/onboarding/complete` at the gate: activation now marks
//    the delegate `pending`, so an activation that never reaches the gate is
//    purged like any other incomplete onboarding (§8.5).
//
// STATUS. The delegate is `partial` for this whole wizard — an owner or admin
// wraps the organization keys only afterwards — so every call it makes must be
// reachable from that state (AuthMiddleware's partial allow-list, §13.3).
//
// M15 SEAM. An invitation may fix the email and/or the phone. `fixedEmail` /
// `fixedPhone` below are the one-line change that prefills and locks the field;
// today's token-based invitation carries no such values.

import { Wizard, createElement, createProgressBar } from '@catalog-shell';
import type { WizardStep, WizardStepContext } from '@catalog-shell';
import type { Content } from 'pdfmake/interfaces';
import { renderDeviceSetup, encryptLabelForDevice } from '../components/device-setup';
import { createEntropyCeremony, type EntropyCeremonyResult } from '../components/entropy-ceremony';
import { buildDelegateKit } from '../components/booklet/delegate-kit';
import { setUpWallet, walletSetupNotice, type WalletSetupResult } from './bits-release';
import { publishOutcomeNotice, publishReceiveAddress } from './receive-address-claim';
import { bitcashWorker } from '../state/bitcash-context';
import { planWalletSetup } from './wallet-setup-plan';
import {
    lockKeyFor, displayWalletKey, generateWalletKeyFrom, TYPE_CASH,
} from '../crypto/bitcash';
import { registrationWalletKey } from '../state/bitcash-wallets-store';
import { setDeviceSlot, rememberSlotKey, DEFAULT_TOKEN_SLOT } from '../state/device-slots';
import { mintedDefaultTokenEntry } from '../key-grant/bitcash-token-cache';
import { fetchRegistrationConfig, type DefaultTokenTerms } from './payment-flow';
import { renderLoginCodeStep } from '../components/login-code-step';
import { base32Encode, base32Decode } from '../crypto/totp';
import { printBooklet, type BookletPrintOptions, type CopySheet } from '../components/booklet/booklet';
import { createPrintButton, renderPrintStage } from '../components/print-button';
import { formatVersion, type AccountHeaderData } from '../components/booklet/shared';
import { DELEGATE_TERMS_ASSET } from '../components/terms-asset';
import { createAgreementLink } from '../components/agreement-link';
import { documentSetPageCount, printerCheckboxLabel } from '../components/booklet/page-estimate';
import { generatePassword, computeAuthHash } from '../crypto/credentials';
import { generateSupersecret } from '../crypto/supersecret';
import { kemKeygen } from '../crypto/kem';
import { signingKeygen } from '../crypto/signing';
import { encryptKeyBag, getKeyBagEntry } from '../crypto/keybag';
import type { KeyBag } from '../crypto/keybag';
import { XRPL_MASTER_KEY_ID } from '../crypto/xrpl';
import * as aes from '../crypto/aes';
import * as accountApi from '../api/account';
import { isTotpRequired } from '../api/types';
import * as delegateApi from '../api/delegate';
import { beginInvitation } from '../api/invitation';
import { deriveInvitationSecrets } from './invitation-document';
import { organizationOrigin, type DelegateInvitationRef } from './delegate-activation-ref';
import * as deviceApi from '../api/device';
import { session } from '../state/session';
import { keys } from '../state/keys';
import { identity } from '../state/identity';
import * as deviceStorage from '../state/device-storage';
import { nodeHostFor, type IdentityOrigin } from '../state/origins';
import {
    emailVerificationStep, phoneVerificationStep, type ClaimStepsConfig,
} from './claim-verification-steps';
import {
    finalizeCredentials, rememberDeviceKey, deviceCopyTarget,
} from './finalize-credentials';
import {
    loadOnboardingStash, saveOnboardingStash, clearOnboardingStash,
    readBitsStash,
} from './onboarding-stash';
import { completeOnboardingThenWipe } from './onboarding-completion';
import { finishLaterFrom, keepsRegistrationOnExit } from './wizard-exit';

export interface DelegateActivationCallbacks {
    onComplete: () => void;
    onCancel: () => void;
}

export interface DelegateActivationOptions {
    /** True when resuming from the login screen's unfinished-registration
     *  banner: state restores from the onboarding stash and the wizard jumps to
     *  the right step. */
    resume?: boolean;
}

export function renderDelegateActivationWizard(
    container: HTMLElement,
    invitation: DelegateInvitationRef,
    callbacks: DelegateActivationCallbacks,
    options: DelegateActivationOptions = {},
): void {
    container.innerHTML = '';

    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card cid-auth-card--wide' });
    wrapper.appendChild(card);
    container.appendChild(wrapper);

    // Shared mutable state — restored from the onboarding stash on resume.
    const shared: Record<string, unknown> = {};
    let resumeFinalize = false;
    if (options.resume) {
        const stash = loadOnboardingStash();
        if (stash && stash.registrationType === 'delegate-activation') {
            Object.assign(shared, stash.shared);
            if (stash.invitationCode) shared['invitationCode'] = stash.invitationCode;
            // A reprint REUSES the sealed sheet: re-issuing bumps the epoch and
            // permanently kills the sheet already on paper (§11).
            if (stash.resetCardSealedToken) shared['resetCardSealedToken'] = stash.resetCardSealedToken;
            if (stash.resetCardCreatedAt) shared['resetCardCreatedAt'] = stash.resetCardCreatedAt;
            resumeFinalize = stash.finalizeStarted === true;
        }
    }
    // The reference the entry page handed in always wins — the invitee may have
    // opened a second, different invitation. The code is all that travels: the
    // lookup id and the fixed-value salt are derived from it at the Activate
    // step, and the fixed email/phone are on the card, not in the link.
    if (invitation.code) shared['invitationCode'] = invitation.code;
    // The organization the card names (docs/cid-governance.md M7a): begin and
    // activate go to ITS origin. Kept in `shared`, so a resume has it too.
    if (invitation.orgCid) shared['invitationOrgCid'] = invitation.orgCid;

    const steps = finishLaterFrom([
        termsStep(shared),
        keygenStep(shared),
        activationStep(shared),
        deviceSetupStep(shared),
        emailVerificationStep(delegateClaimSteps(shared)),
        phoneVerificationStep(delegateClaimSteps(shared)),
        finalizeStep(shared),
    ], 'activate');
    const wizard = new Wizard(card, {
        steps,
        onComplete: () => callbacks.onComplete(),
        onCancel: () => {
            // Before activation: an explicit discard — no throwaway credentials
            // linger, and the invitation is still unspent.
            // From the step that activates the account on, the button says
            // "Finish later" and the stash stays — it is the only way back in, and
            // an account left unfinished is purged after 7 days (auth/wizard-exit.ts).
            if (!keepsRegistrationOnExit(steps, wizard.getCurrentIndex())) clearOnboardingStash();
            callbacks.onCancel();
        },
        showCancel: true,
    });

    if (options.resume && shared['delegateCid']) {
        void (async () => {
            await ensureResumedDelegateSession(shared);
            if (resumeFinalize || shared['phoneVerified']) {
                wizard.goTo('finalize');
            } else {
                // The activation step's guard auto-advances when the delegate
                // CID already exists; the following steps walk forward on theirs.
                wizard.goTo('activate');
            }
        })();
    }
}

// ============================================================================
// Stash + resume helpers
// ============================================================================

/** Write the current wizard state into the onboarding stash. Same record, same
 *  key and same 7-day TTL as the two registration wizards use — so the login
 *  screen's "unfinished registration" banner covers an interrupted activation
 *  too — distinguished by `registrationType`. */
function stashSync(shared: Record<string, unknown>, patch: {
    finalizeStarted?: boolean;
    resetCardSealedToken?: string;
    resetCardCreatedAt?: string;
    totpStored?: boolean;
} = {}): void {
    const prior = loadOnboardingStash();
    const priorActivation = prior?.registrationType === 'delegate-activation' ? prior : undefined;
    saveOnboardingStash({
        v: 1,
        savedAt: Date.now(),
        registrationType: 'delegate-activation',
        invitationCode: (shared['invitationCode'] as string | undefined) ?? priorActivation?.invitationCode,
        finalizeStarted: patch.finalizeStarted ?? priorActivation?.finalizeStarted,
        resetCardSealedToken: patch.resetCardSealedToken
            ?? (shared['resetCardSealedToken'] as string | undefined) ?? priorActivation?.resetCardSealedToken,
        resetCardCreatedAt: patch.resetCardCreatedAt
            ?? (shared['resetCardCreatedAt'] as string | undefined) ?? priorActivation?.resetCardCreatedAt,
        totpStored: patch.totpStored ?? priorActivation?.totpStored,
        shared,
    });
}

/**
 * Make sure a resumed activation is authorized and has keys in memory. The
 * localStorage session token usually survives; when it does not, log in with
 * the THROWAWAY password from the stash (the invitee never knew it — that is
 * the point of stashing it). After set-authhash the throwaway is dead, but that
 * call also deliberately keeps the calling session alive.
 */
async function ensureResumedDelegateSession(shared: Record<string, unknown>): Promise<void> {
    const cid = shared['delegateCid'] as string | undefined;
    if (!cid) return;

    if (shared['keyBag']) keys.load(shared['keyBag'] as KeyBag);

    try {
        await accountApi.getAccountInfo();
        return; // session alive
    } catch { /* dead session — revive below */ }

    try {
        const authHash = await computeAuthHash(String(shared['password'] ?? ''), cid);
        const resp = await accountApi.login({ cid, authHash });
        // A throwaway-password revival can never meet the Authenticator Key
        // gate — finalize stores the key with the gate OFF, and the optional
        // "Login code" step runs after the print gate in the same page
        // lifetime. If it somehow answers `totp_required` there is no session
        // to revive with, so fall through to the same handling as a failure.
        if (isTotpRequired(resp)) throw new Error('This account now requires an authenticator code to sign in');
        session.start(resp.sessionToken, {
            cid: resp.cid, accountType: resp.accountType, status: resp.status, sessionId: resp.sessionId,
        });
        identity.setActive({
            cid: resp.cid, accountType: 'delegate',
            organizationCid: shared['orgCid'] as string | undefined,
        });
        deviceStorage.setActiveCid(resp.cid);
    } catch {
        // Neither session nor throwaway login works (e.g. a crash mid-finalize
        // after set-authhash, with the session since expired). The print step
        // surfaces the API errors; the fallback is the organization's owner.
    }
}

// ── the invited delegate's claim steps ──────────────────────────────────────
// The shared implementation (auth/claim-verification-steps.ts), pointed at this
// delegate's own attr_encrypt pair.
//
// `emailFixed`/`phoneFixed` come from what `begin` reported about the
// invitation, and only add a note — the fields stay empty and editable, because
// since §14.2 the plaintext travels on the CARD and nowhere else. `valueSalt` is
// derived from the code at the Activate step and is what lets the validator
// check what the member typed against the digest the inviter signed.

function delegateClaimSteps(shared: Record<string, unknown>): ClaimStepsConfig {
    return {
        shared,
        stashSync: () => stashSync(shared),
        attrPublicKey: () => shared['attrPublicKey'] as string,
        attrPrivateKey: () => getKeyBagEntry(shared['keyBag'] as KeyBag, 'attr_encrypt')!.privateKey,
        emailFixed: () => shared['invitationEmailFixed'] === true,
        phoneFixed: () => shared['invitationPhoneFixed'] === true,
        valueSalt: () => shared['invitationValueSalt'] as string | undefined,
        copy: {
            emailIntro: 'Add an email address for your delegate account and verify it. Activation '
                + 'completes after both your email and phone are verified.',
            phoneIntro: 'Add a phone number that can receive SMS, and verify it with the code we '
                + 'text you.',
        },
    };
}

// ============================================================================
// Step 1: Terms
// ============================================================================

function termsStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'terms',
        label: 'Terms',
        render(container) {
            const step = createElement('div', { className: 'cid-wizard-step' });

            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Delegate Terms' }));

            const text = createElement('div', { className: 'cid-wizard-terms' });
            text.textContent = 'By activating your delegate identity, you agree to the Catalog.ID Delegate Terms, '
                + 'which are printed in full in the kit at the end of this process. '
                + 'Your identity is generated entirely in your browser. The server never receives your private keys. '
                + 'Your credentials are printed — once — at the end of activation; you are solely responsible '
                + 'for keeping that printed kit safe. If you lose your supersecret and have no device with '
                + 'imported keys, your identity is irrecoverable by design. '
                + 'As a delegate, your identity is managed within the context of your organization.';
            step.appendChild(text);
            // The agreement itself, to read before accepting it (2026-10-15).
            step.appendChild(createAgreementLink(DELEGATE_TERMS_ASSET));

            const checkRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = 'terms-accept';
            if (shared['termsAccepted']) checkbox.checked = true;
            const label = createElement('label', { textContent: 'I have read and accept the Delegate Terms' });
            label.setAttribute('for', 'terms-accept');
            checkRow.appendChild(checkbox);
            checkRow.appendChild(label);
            step.appendChild(checkRow);

            checkbox.addEventListener('change', () => { shared['termsAccepted'] = checkbox.checked; });

            // Activation ends at a printer — there is no download and no second
            // chance, so ask BEFORE any keys move.
            const printerRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const printerCheckbox = document.createElement('input');
            printerCheckbox.type = 'checkbox';
            printerCheckbox.id = 'terms-printer';
            if (shared['printerAvailable']) printerCheckbox.checked = true;
            const printerLabel = createElement('label', {
                // The size, so the member can tell whether their printer is up to it.
                textContent: printerCheckboxLabel('delegate'),
            });
            printerLabel.setAttribute('for', 'terms-printer');
            printerRow.appendChild(printerCheckbox);
            printerRow.appendChild(printerLabel);
            step.appendChild(printerRow);

            printerCheckbox.addEventListener('change', () => { shared['printerAvailable'] = printerCheckbox.checked; });

            container.appendChild(step);
        },
        validate() {
            if (shared['termsAccepted'] !== true) return 'You must accept the Delegate Terms';
            return shared['printerAvailable'] === true
                || 'Confirm that you have a printer available — the document set is printed, never downloaded';
        },
    };
}

// ============================================================================
// Step 2: Identity Generation (throwaway credentials — invitee never sees them)
// ============================================================================

function keygenStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'keygen',
        label: 'Identity',
        hideNext: true,
        render(container, ctx) {
            if (shared['keyBag']) {
                container.appendChild(createElement('p', {
                    className: 'cid-wizard-text', textContent: 'Identity material already generated.',
                }));
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Generating Your Delegate Identity' }));

            const progress = createProgressBar({ indeterminate: true, label: 'Generating keys...' });
            step.appendChild(progress.element);

            const statusEl = createElement('p', { className: 'cid-wizard-status' });
            step.appendChild(statusEl);

            container.appendChild(step);

            void (async () => {
                try {
                    // Working credentials for the activation machinery only —
                    // finalize-at-print replaces both with ceremony-minted values
                    // the moment they go on paper (§3). NO `xrpl_master` here:
                    // the ceremony mints it and finalize appends it (§13.1).
                    statusEl.textContent = 'Generating working credentials...';
                    const password = generatePassword();
                    const supersecret = generateSupersecret();
                    shared['password'] = password;
                    shared['supersecret'] = supersecret;

                    statusEl.textContent = 'Generating attr_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const attrKP = kemKeygen();

                    statusEl.textContent = 'Generating msg_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const msgKP = kemKeygen();

                    statusEl.textContent = 'Generating efs_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const efsKP = kemKeygen();

                    statusEl.textContent = 'Generating main_signing_key pair (Ed25519 + ML-DSA-65)...';
                    const sigKP = signingKeygen();

                    statusEl.textContent = 'Generating chat_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const chatEncKP = kemKeygen();

                    statusEl.textContent = 'Generating chat_sign key pair (Ed25519 + ML-DSA-65)...';
                    const chatSigKP = signingKeygen();

                    statusEl.textContent = 'Generating mail_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const mailEncKP = kemKeygen();

                    statusEl.textContent = 'Generating mail_sign key pair (Ed25519 + ML-DSA-65)...';
                    const mailSigKP = signingKeygen();

                    const keyBag: KeyBag = {
                        entries: [
                            { keyId: 'attr_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(attrKP.secretKey) },
                            { keyId: 'msg_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(msgKP.secretKey) },
                            { keyId: 'efs_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(efsKP.secretKey) },
                            { keyId: 'main_signing_key', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(sigKP.secretSeed) },
                            { keyId: 'chat_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(chatEncKP.secretKey) },
                            { keyId: 'chat_sign', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(chatSigKP.secretSeed) },
                            { keyId: 'mail_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(mailEncKP.secretKey) },
                            { keyId: 'mail_sign', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(mailSigKP.secretSeed) },
                        ],
                    };
                    shared['keyBag'] = keyBag;
                    shared['attrPublicKey'] = aes.toBase64(attrKP.publicKey);
                    // Local surfaces carry every public half, `chat_sign` /
                    // `mail_sign` included; the server payload below omits the
                    // bag-only ones.
                    shared['publicKeys'] = {
                        attr_encrypt: aes.toBase64(attrKP.publicKey),
                        msg_encrypt: aes.toBase64(msgKP.publicKey),
                        efs_encrypt: aes.toBase64(efsKP.publicKey),
                        main_signing_key: aes.toBase64(sigKP.publicKey),
                        chat_encrypt: aes.toBase64(chatEncKP.publicKey),
                        chat_sign: aes.toBase64(chatSigKP.publicKey),
                        mail_encrypt: aes.toBase64(mailEncKP.publicKey),
                        mail_sign: aes.toBase64(mailSigKP.publicKey),
                    };

                    statusEl.textContent = 'Encrypting key bag with the working supersecret (Argon2id)...';
                    progress.setLabel('Encrypting key bag...');
                    const encrypted = await encryptKeyBag(keyBag, supersecret);
                    shared['encryptedKeyBag'] = encrypted;

                    // The stash starts here — from now on a crash can resume.
                    stashSync(shared);

                    progress.setIndeterminate(false);
                    progress.setValue(100);
                    progress.setVariant('success');
                    progress.setLabel('Keys generated');
                    statusEl.textContent = '';

                    setTimeout(() => ctx.wizard.next(), 300);
                } catch (err) {
                    progress.setIndeterminate(false);
                    progress.setVariant('error');
                    progress.setLabel('Failed');
                    statusEl.textContent = err instanceof Error ? err.message : 'Key generation failed';
                    statusEl.className = 'cid-pane-error cid-pane-error--visible';
                }
            })();
        },
    };
}

// ============================================================================
// Step 3: Activation (invitation lookup + /api/delegate/activate)
// ============================================================================
//
// A coded invitation (§14.2) is resolved here: one scrypt over the code turns it
// into the lookup id `begin`/`activate` are addressed by, and into the
// fixed-value salt the claim steps hand to the validator. Both are derived on
// THIS device and the salt never leaves it in readable form.
//
// What the wizard registers is the THROWAWAY authHash, replaced at the print
// step. (The pre-M15 `?token=` lookup/activate pair was removed in M20.)

function activationStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'activate',
        label: 'Activate',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            if (shared['delegateCid']) {
                container.appendChild(createElement('p', {
                    className: 'cid-wizard-text',
                    textContent: `Your delegate identity ${shared['delegateCid']} is activated.`,
                }));
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Activating Your Delegate Identity' }));

            const progress = createProgressBar({ indeterminate: true, label: 'Activating...' });
            step.appendChild(progress.element);

            const statusEl = createElement('p', { className: 'cid-wizard-status' });
            step.appendChild(statusEl);

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            step.appendChild(errorEl);

            container.appendChild(step);

            void (async () => {
                try {
                    const keyBag = shared['keyBag'] as KeyBag;
                    const publicKeys = shared['publicKeys'] as Record<string, string>;
                    const encrypted = shared['encryptedKeyBag'] as Awaited<ReturnType<typeof encryptKeyBag>>;
                    const invitationCode = shared['invitationCode'] as string | undefined;
                    const orgCid = shared['invitationOrgCid'] as string | undefined;

                    // The CID is needed before /activate — authHash is
                    // Argon2id(password, SHA-256(cid)). The code draws it through
                    // begin, which is idempotent: a resume gets the same CID back
                    // (§13.5).
                    let lookupId: string;
                    let delegateCidForHash: string;
                    let origin: IdentityOrigin;
                    if (invitationCode && !orgCid) {
                        throw new Error('This invitation does not say which organization it is for. Open the '
                            + 'link on your card again, or enter the code and the Organization ID printed on '
                            + 'the card on the Join page.');
                    }
                    if (invitationCode && orgCid) {
                        // ~0.1 s and 32 MiB, which is the point (§14.2): it is
                        // what makes the node's stored hashes useless to anybody
                        // who does not hold this code.
                        statusEl.textContent = 'Reading your invitation code...';
                        const secrets = await deriveInvitationSecrets(invitationCode);
                        lookupId = secrets.lookupIdHex;
                        // Kept for the claim steps, which seal it to the
                        // validator; deriving it again per claim would cost
                        // another scrypt for the same answer.
                        shared['invitationValueSalt'] = secrets.saltHex;

                        // The invitation lives only at the organization's home
                        // server, and so will this delegate's sessions (M7a).
                        statusEl.textContent = 'Finding your organization...';
                        origin = await organizationOrigin(orgCid);

                        statusEl.textContent = 'Looking up your delegate identity...';
                        const begun = await beginInvitation(lookupId, orgCid, origin.url);
                        delegateCidForHash = begun.delegateCid;
                        // WHETHER each channel is fixed, never the values: they
                        // are printed on the card and typed by the invitee.
                        shared['invitationEmailFixed'] = begun.emailFixed;
                        shared['invitationPhoneFixed'] = begun.phoneFixed;
                    } else {
                        throw new Error('This link carries no invitation');
                    }

                    statusEl.textContent = 'Computing auth hash (Argon2id)...';
                    const authHash = await computeAuthHash(shared['password'] as string, delegateCidForHash);

                    statusEl.textContent = 'Activating delegate identity...';
                    const resp = await delegateApi.activateDelegate({
                        lookupId,
                        delegateCid: delegateCidForHash,
                        authHash,
                        keySalt: encrypted.keySalt,
                        // `chat_sign` and `mail_sign` are absent by design — bag-only,
                        // see BAG_ONLY_KEY_IDS. `mail_encrypt` IS here: the mail
                        // directory hands its public half out at resolve time.
                        publicKeys: [
                            { keyId: 'attr_encrypt', algorithm: 'X25519-MLKem1024', publicKey: publicKeys['attr_encrypt'] },
                            { keyId: 'msg_encrypt', algorithm: 'X25519-MLKem1024', publicKey: publicKeys['msg_encrypt'] },
                            { keyId: 'efs_encrypt', algorithm: 'X25519-MLKem1024', publicKey: publicKeys['efs_encrypt'] },
                            { keyId: 'main_signing_key', algorithm: 'Ed25519-MLDsa65', publicKey: publicKeys['main_signing_key'] },
                            { keyId: 'chat_encrypt', algorithm: 'X25519-MLKem1024', publicKey: publicKeys['chat_encrypt'] },
                            { keyId: 'mail_encrypt', algorithm: 'X25519-MLKem1024', publicKey: publicKeys['mail_encrypt'] },
                        ],
                        keyBagEntries: encrypted.entries,
                    }, origin.url);

                    shared['delegateCid'] = resp.delegateCid;
                    session.start(resp.sessionToken, {
                        cid: resp.delegateCid, accountType: 'delegate', status: 'partial', sessionId: resp.sessionId,
                    }, origin);
                    identity.setActive({ cid: resp.delegateCid, accountType: 'delegate' });
                    keys.load(keyBag);
                    deviceStorage.setActiveCid(resp.delegateCid);

                    // Organization rows for the kit's delegate header. The
                    // invitation lookup carries only the delegate CID, so they
                    // come from the account record. Best-effort: a failure here
                    // must never fail an activation that already succeeded — the
                    // header just prints without those rows.
                    try {
                        const me = await accountApi.getAccountInfo();
                        shared['orgCid'] = me.organizationCid ?? undefined;
                        shared['orgRole'] = me.roles?.[0] ?? undefined;
                        identity.setActive({
                            cid: resp.delegateCid, accountType: 'delegate',
                            organizationCid: me.organizationCid ?? undefined,
                            roles: me.roles ?? undefined,
                        });
                    } catch {
                        // Header prints without the Organization / Role rows.
                    }

                    // The invitation is single-use: stash the CID before anything
                    // else can fail, so a resume never redeems the token twice.
                    stashSync(shared);

                    progress.setIndeterminate(false);
                    progress.setValue(100);
                    progress.setVariant('success');
                    progress.setLabel('Delegate identity activated');
                    statusEl.textContent = '';

                    step.appendChild(createElement('p', {
                        className: 'cid-wizard-cid', textContent: `Your Catalog.ID: ${resp.delegateCid}`,
                    }));

                    setTimeout(() => ctx.wizard.next(), 1200);
                } catch (err) {
                    progress.setIndeterminate(false);
                    progress.setVariant('error');
                    progress.setLabel('Activation failed');
                    statusEl.textContent = '';
                    errorEl.textContent = err instanceof Error ? err.message : 'Activation failed';
                    errorEl.classList.add('cid-pane-error--visible');

                    const retryBtn = createElement('button', {
                        className: 'cs-btn cs-btn--default cid-auth-submit',
                        textContent: 'Retry',
                    });
                    retryBtn.addEventListener('click', () => {
                        container.innerHTML = '';
                        activationStep(shared).render(container, ctx);
                    });
                    step.appendChild(retryBtn);
                }
            })();
        },
    };
}

// ============================================================================
// Step 4: Device Setup (before the print — device-held keys are the crash net)
// ============================================================================

function deviceSetupStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'device',
        label: 'Device',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            if (shared['deviceSetupComplete']) {
                container.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: 'Device configured.' }));
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Device Setup' }));

            renderDeviceSetup(step, shared['keyBag'] as KeyBag, shared['delegateCid'] as string, {
                onSetup: async (
                    params: Parameters<import('../components/device-setup').DeviceSetupCallbacks['onSetup']>[0],
                    manifest: Parameters<import('../components/device-setup').DeviceSetupCallbacks['onSetup']>[1],
                ) => {
                    // `label` is plaintext — encrypt it here; never forward it as-is.
                    const { label: plainLabel, ...rest } = params;
                    const cid = shared['delegateCid'] as string;
                    const labelFields = await encryptLabelForDevice(cid, plainLabel);
                    const resp = await deviceApi.setupDevice({ ...rest, ...labelFields });
                    deviceStorage.setDeviceId(cid, resp.deviceId);
                    // Keep the DEK for the print step: the ceremony mints
                    // `xrpl_master` AFTER this point, and its device copy has to
                    // be encrypted under this same key (§11 trap, §13.1) — and
                    // under the same key SELECTION, so a key the member just
                    // declined is not written here a minute later
                    // (device-setup-restructure §8, §7.3).
                    rememberDeviceKey(shared, cid, params.deviceEncryptionKey, manifest);
                    // …and give the device-slot store the same key, so the
                    // default token the print step is about to mint can be
                    // cached under it (registration-wallets §4.12).
                    rememberSlotKey(cid, aes.fromBase64(params.deviceEncryptionKey));
                    shared['deviceSetupComplete'] = true;
                    stashSync(shared);
                    ctx.wizard.next();
                },
                onSkip: () => {
                    deviceStorage.setNoLocalStorageMode(shared['delegateCid'] as string);
                    shared['deviceSetupComplete'] = true;
                    stashSync(shared);
                    ctx.wizard.next();
                },
            }, { confirmLabel: 'Configure device and continue' });

            container.appendChild(step);
        },
    };
}

// ============================================================================
// Step 7: Print (issue Unlock Sheet → ceremony → finalize → print → gate)
// ============================================================================

function finalizeStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'finalize',
        label: 'Print',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Create & Print Your Credentials' }));

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            const statusEl = createElement('p', { className: 'cid-wizard-status' });

            let ceremonyResult: EntropyCeremonyResult | null = null;
            /** Set when the bits did not arrive — shown beside the kit (§4.5). */
            let walletNotice: string | null = null;
            /** What became of "Publish my receive address", when it was ticked. */
            let publishNotice: string | null = null;
            /** D27, from the node's config; null until read, or when the node names none (M12, D15). */
            let defaultTokenCaps: DefaultTokenTerms | null = null;
            void fetchRegistrationConfig().then(cfg => { defaultTokenCaps = cfg.defaultToken; });

            const ceremony = createEntropyCeremony({
                onReady: (result) => {
                    ceremonyResult = result;
                    finalizeBtn.style.display = result ? '' : 'none';
                },
                // An invited delegate's ceremony draws ONE wallet, like a
                // personal registration: the organization's own wallet was
                // minted by the org's founding ceremony, and this delegate gets
                // their own (§4.13).
                mintWalletKey: (next) => generateWalletKeyFrom(next, TYPE_CASH),
            });
            step.appendChild(ceremony);

            const finalizeBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes cid-auth-submit',
                textContent: 'Finalize credentials',
            });
            finalizeBtn.style.display = 'none';
            step.appendChild(finalizeBtn);
            step.appendChild(statusEl);
            step.appendChild(errorEl);

            // The publish checkbox (D4, default off).
            const publishRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const publishBox = document.createElement('input');
            publishBox.type = 'checkbox';
            publishBox.id = 'cid-delegate-publish-fund-address';
            publishBox.checked = false;
            const publishLabel = createElement('label', {
                textContent: 'Publish my receive address so others can pay me',
            });
            publishLabel.setAttribute('for', publishBox.id);
            publishRow.appendChild(publishBox);
            publishRow.appendChild(publishLabel);
            step.appendChild(publishRow);
            const publishHint = createElement('p', {
                className: 'cid-pane-note',
                textContent: 'Your receive address can only receive — it cannot spend. Publishing it '
                    + 'puts it on your public profile; you can change that later in Settings.',
            });
            step.appendChild(publishHint);

            // Where to print is said beside the print button(s), once there is
            // something to print (components/print-button.ts PHYSICAL_PRINTER_NOTE).

            container.appendChild(step);

            finalizeBtn.addEventListener('click', async () => {
                if (!ceremonyResult) return;
                const final = ceremonyResult;
                finalizeBtn.setAttribute('disabled', '');
                errorEl.classList.remove('cid-pane-error--visible');

                try {
                    const cid = shared['delegateCid'] as string;
                    const keyBag = shared['keyBag'] as KeyBag;

                    // 1. Account Unlock Sheet — reuse an already-issued token
                    //    (bump-on-issue: re-issuing would kill a sheet from a
                    //    previous print attempt, §11).
                    let sealedToken = shared['resetCardSealedToken'] as string | undefined;
                    if (!sealedToken) {
                        statusEl.textContent = 'Sealing your Account Unlock Sheet...';
                        const cardResp = await accountApi.issueResetCard({
                            emailClaimUuid: shared['emailClaimUuid'] as string,
                            emailEncryptedKey: shared['emailClaimEncKey'] as string,
                            phoneClaimUuid: shared['phoneClaimUuid'] as string,
                            phoneEncryptedKey: shared['phoneClaimEncKey'] as string,
                        });
                        sealedToken = cardResp.sealedToken;
                        shared['resetCardSealedToken'] = sealedToken;
                        shared['resetCardCreatedAt'] = cardResp.createdAt;
                        stashSync(shared, {
                            resetCardSealedToken: sealedToken, resetCardCreatedAt: cardResp.createdAt,
                        });
                    }

                    // 2. The shared finalize sequence (auth/finalize-credentials.ts):
                    //    append the ceremony's `xrpl_master` unless the bag already
                    //    carries one (resume rule §13.1), re-encrypt the bag under
                    //    the FINAL supersecret, replace it server-side, copy the new
                    //    entry onto this device, and set the final auth hash last.
                    stashSync(shared, { finalizeStarted: true });
                    // Round 8 (§15.6): the Authenticator Key, PUT under the
                    // PARTIAL session (allowed — the activation wizard finalizes
                    // in that state). RESUME RULE: once the node holds it, never
                    // PUT again or the printed sheet is void.
                    const priorTotp = readStashedTotpSecret(shared);
                    const totpAlreadyStored = loadOnboardingStash()?.totpStored === true && priorTotp !== null;
                    const totpSecret = totpAlreadyStored ? priorTotp : final.totpSecret;
                    const finalized = await finalizeCredentials({
                        cid,
                        bag: keyBag,
                        supersecret: final.supersecret,
                        password: final.password,
                        xrplSeedEntropy: final.xrplSeedEntropy,
                        totpSecret,
                        totpAlreadyStored,
                        // This delegate's own wallet (§4.8).
                        bitcashWalletKey: final.bitcashWalletKey ?? null,
                        device: deviceCopyTarget(shared),
                        onStatus: (message) => { statusEl.textContent = message; },
                    });
                    // The bag the account now has — the XRPL sheet prints ITS key,
                    // a reprint reuses it, and a retry after a failure below must
                    // not mint a second one.
                    shared['keyBag'] = finalized.bag;
                    keys.load(finalized.bag);
                    if (finalized.totpStored && totpSecret) {
                        shared['totpSecret'] = base32Encode(totpSecret);
                    }
                    stashSync(shared, { finalizeStarted: true, totpStored: finalized.totpStored });
                    // "No local storage" mode holds no keys here by choice — only
                    // a device that DOES hold keys and missed the copy is a gap.
                    if (finalized.deviceCopyMissing && !deviceStorage.isNoLocalStorageMode(cid)) {
                        console.warn('[delegate-activation] the XRPL master key was not copied to this '
                            + 'device — its first use here will ask for the supersecret');
                    }

                    // 2b. The money leg (§4.5), under the PARTIAL session this
                    //     wizard finalizes in. An invited delegate is the INVITEE
                    //     path: the bits were moved onto the invitation's lock
                    //     when the owner issued the card, so the INVITATION's
                    //     seed is what opens them — the voucher's key stayed with
                    //     the organization as its reclaim path (§4.4).
                    if (finalized.bitcashWallet) {
                        // What the kit sheet prints (§13.1) — the document's
                        // wallet, which is the one the bits are released into.
                        shared['bitcashWalletKey'] = finalized.bitcashWallet.key;
                        const invitationCode = shared['invitationCode'] as string | undefined;
                        const secrets = invitationCode
                            ? await deriveInvitationSecrets(invitationCode) : null;
                        // Ledger, ref and codes written down before any ledger call (M12).
                        const plan = await planWalletSetup({
                            walletKey: finalized.bitcashWallet.key, stash: shared,
                            stashKeys: { fund: 'bitsFundCode', token: 'bitsTokenCode' },
                        });
                        stashSync(shared);
                        // The default token's terms: caps and service types (it is minted unbound, §9.3).
                        const tokenTerms = defaultTokenCaps ?? (await fetchRegistrationConfig()).defaultToken;
                        const setup: WalletSetupResult = plan === null
                            ? { release: null, receiveAddr: null, defaultToken: null, walletMissing: true }
                            : await setUpWallet({
                                walletKey: finalized.bitcashWallet.key,
                                walletRef: plan.walletRef,
                                networkId: plan.networkId,
                                lock: secrets
                                    ? { lockKey: lockKeyFor(secrets.publicKey, 0), seed: secrets.seed }
                                    : undefined,
                                caps: tokenTerms,
                                instruments: plan.instruments,
                                alreadyReleased: readBitsStash(shared).bitsReleased,
                                alreadyTokenIssued: readBitsStash(shared).defaultTokenIssued,
                                onStatus: (message) => { statusEl.textContent = message; },
                            }, { worker: bitcashWorker() });

                        if (setup.release?.status === 'released') {
                            shared['bitsReleased'] = true;
                            shared['bitsWalletRef'] = setup.release.walletRef;
                        }
                        if (plan !== null) walletNotice = walletSetupNotice(setup);
                        if (setup.receiveAddr) shared['bitsFundAddress'] = setup.receiveAddr;
                        if (setup.defaultToken) {
                            shared['defaultTokenIssued'] = true;
                            // The key-grant handler's own entry, with the scope it was minted with (§9.3).
                            await setDeviceSlot(DEFAULT_TOKEN_SLOT, mintedDefaultTokenEntry({
                                token: setup.defaultToken, walletRef: plan?.walletRef,
                                serviceTypes: tokenTerms?.serviceTypes ?? [],
                            }), cid);
                        }
                        if (publishBox.checked && setup.receiveAddr) {
                            statusEl.textContent = 'Publishing your receive address…';
                            const published = await publishReceiveAddress({
                                receiveAddr: setup.receiveAddr,
                                attrPublicKey: aes.fromBase64(
                                    (shared['publicKeys'] as Record<string, string>)['attr_encrypt']),
                                // The sharer's half of the ECDH that re-wraps the
                                // claim key for each operator. Read from the bag
                                // the account now HAS, not from the ceremony.
                                attrPrivateKey: aes.fromBase64(getKeyBagEntry(
                                    shared['keyBag'] as KeyBag, 'attr_encrypt')!.privateKey),
                            });
                            shared['publishFundAddress'] = published.status === 'published';
                            publishNotice = publishOutcomeNotice(published);
                        }
                        stashSync(shared);
                    }

                    // A ticked box with no address to publish (no wallet) says so too.
                    if (publishBox.checked && publishNotice === null) publishNotice = publishOutcomeNotice(null);

                    // 3. The kit is ready; printing is a click of its own
                    //    (components/print-button.ts). The print window must open
                    //    inside a click, and THIS click's ran out long ago — the
                    //    sequence above takes seconds to a minute (staging test
                    //    2026-10-16). Whatever the kit needs is fetched now, never
                    //    inside that click.
                    await refreshStashedWalletKey(shared);
                    const sealed = sealedToken;
                    const printKit = (): Promise<void> => {
                        const { pages, copySheets, print } = buildPages(shared, final, sealed);
                        return printBooklet(pages, copySheets, print);
                    };

                    statusEl.textContent = '';
                    for (const notice of [walletNotice, publishNotice]) {
                        if (notice) step.appendChild(createElement('p', { className: 'cid-pane-note', textContent: notice }));
                    }
                    finalizeBtn.style.display = 'none';
                    ceremony.style.display = 'none';
                    // The publish choice was read by Finalize: from here on the box
                    // would only look like a choice still open.
                    publishRow.style.display = 'none';
                    publishHint.style.display = 'none';
                    // The exact size, now that the document exists (the Terms step
                    // could only say "about").
                    const sized = buildPages(shared, final, sealed);
                    const pageCount = documentSetPageCount(sized.pages, sized.copySheets, sized.print);
                    renderPrintStage(step, {
                        intro: `Your credentials are set. Print your Delegate Membership Kit now — ${pageCount} `
                            + 'pages. The next step asks you to type back a line from it.',
                        label: 'Print document set',
                        print: printKit,
                        onPrinted: () => renderTypeBackGate(step, shared, final, printKit, ctx),
                    });
                } catch (err) {
                    finalizeBtn.removeAttribute('disabled');
                    statusEl.textContent = '';
                    errorEl.textContent = err instanceof Error ? err.message : 'Finalize failed';
                    errorEl.classList.add('cid-pane-error--visible');
                }
            });
        },
    };
}

/** The invited delegate's Catalog.ID Delegate Membership Kit — the same shared
 *  builder the organization wizard prints for the founding delegate, so the two
 *  documents are identical: cover, introduction, delegate credentials with the
 *  Contact row, the Account Unlock Sheet, the XRPL Master Key sheet, and the
 *  Delegate Terms appended at print time.
 *
 *  `orgCid`/`orgRole` come from the post-activation account lookup and may be
 *  missing; `orgDomain` has no client-side source on this path. The header
 *  simply omits whichever rows are absent. */
function buildPages(
    shared: Record<string, unknown>,
    final: EntropyCeremonyResult,
    sealedTokenB64: string | undefined,
): { pages: Content[][]; copySheets: CopySheet[]; print: BookletPrintOptions } {
    const header: AccountHeaderData = {
        cid: String(shared['delegateCid'] ?? ''),
        accountTypeLabel: 'Delegate',
        version: formatVersion(new Date()),
        orgCid: shared['orgCid'] as string | undefined,
        orgDomain: shared['orgDomain'] as string | undefined,
        orgRole: shared['orgRole'] as string | undefined,
    };

    // The key the bag ACTUALLY holds: the finalize sequence put the ceremony's
    // entry there, and a resumed bag keeps the one it already had (§13.1). A bag
    // without one prints no sheet rather than failing.
    const xrplEntry = getKeyBagEntry(shared['keyBag'] as KeyBag, XRPL_MASTER_KEY_ID);
    // Same rule for the wallet (§4.8), against what the account really holds —
    // the `cid/bitcash-wallets` document (cid-encrypted-storage §7.2), read
    // into the stash by `refreshStashedWalletKey` just before this runs.

    const { pages, copySheets, print } = buildDelegateKit({
        header,
        password: final.password,
        supersecret: final.supersecret,
        contact: {
            email: shared['emailAddress'] as string | undefined,
            phone: shared['phoneNumber'] as string | undefined,
        },
        unlockSheetToken: sealedTokenB64 ? aes.fromBase64(sealedTokenB64) : null,
        // The node that holds the account — the organization's origin, which
        // need not be the node this client entered through (M7a).
        nodeHost: nodeHostFor(String(shared['delegateCid'] ?? '')),
        xrplSeedEntropy: xrplEntry ? aes.fromBase64(xrplEntry.privateKey) : null,
        // Only what the node holds (§15.6); a reprint prints the stash's secret.
        totpSecret: readStashedTotpSecret(shared),
        bitcashWalletKey: stashedWalletKey(shared),
        bitcashReceiveAddress: typeof shared['bitsFundAddress'] === 'string'
            ? shared['bitsFundAddress'] as string : null,
        bitcashBitsPaidIn: shared['bitsReleased'] === true,
        terms: DELEGATE_TERMS_ASSET,
    });
    return { pages, copySheets, print };
}

/** The Authenticator Key the node already holds for this delegate, read back
 *  out of the stash's `shared` map (base32). Null when there is none. */
function readStashedTotpSecret(shared: Record<string, unknown>): Uint8Array | null {
    const value = shared['totpSecret'];
    if (typeof value !== 'string' || !value) return null;
    try {
        return base32Decode(value);
    } catch {
        return null;
    }
}

/** The type-back print gate (§3.2 step 6): one supersecret line retyped from
 *  the paper proves a legible print exists. Passing it marks the delegate's
 *  onboarding complete server-side (§8.5 — activation now stamps `pending`, so
 *  an activation that never reaches here IS purged) and only THEN wipes the
 *  stash. */
function renderTypeBackGate(
    step: HTMLElement,
    shared: Record<string, unknown>,
    final: EntropyCeremonyResult,
    /** Prints the same kit again — started synchronously in its click. */
    printKit: () => Promise<void>,
    ctx: WizardStepContext,
): void {
    const gate = createElement('div', { className: 'cid-wizard-step' });
    const lineNo = 1 + Math.floor(Math.random() * 4);
    const cleaned = final.supersecret.replace(/[-\s]/g, '');
    const expected = cleaned.slice((lineNo - 1) * 12, lineNo * 12);

    gate.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Confirm Your Print' }));
    gate.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: `Take the printed document set and type LINE ${lineNo} of the supersecret exactly as it `
            + 'appears on the Credentials page. This confirms the print is complete and legible — '
            + 'after this step, the credentials exist nowhere but on your paper.',
    }));

    const fieldWrapper = createElement('div', { className: 'cid-auth-field' });
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'cid-auth-input';
    input.placeholder = `Supersecret line ${lineNo} (12 characters)`;
    input.autocomplete = 'off';
    input.spellcheck = false;
    fieldWrapper.appendChild(input);
    gate.appendChild(fieldWrapper);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    gate.appendChild(errorEl);

    const confirmBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Confirm print',
    });
    confirmBtn.addEventListener('click', () => {
        const typed = input.value.replace(/[-\s]/g, '');
        if (typed !== expected) {
            errorEl.textContent = typed.length !== 12
                ? 'That is not 12 characters — type the full line, dashes optional.'
                : 'That does not match the printed line — check for look-alike characters and try again, or reprint.';
            errorEl.classList.add('cid-pane-error--visible');
            return;
        }
        void confirmCompletion();
    });
    gate.appendChild(confirmBtn);

    // Shown only after a failed completion call: repeats the CALL, not the gate
    // — the typed line already matched, so re-typing it proves nothing.
    const retryBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Retry',
    });
    retryBtn.style.display = 'none';
    retryBtn.addEventListener('click', () => { void confirmCompletion(); });
    gate.appendChild(retryBtn);

    gate.appendChild(createPrintButton({
        label: 'Print again', className: 'cs-btn cs-btn--default', print: printKit, errorEl,
    }));

    step.appendChild(gate);

    /** Tell the server onboarding is done, THEN wipe the stash — on failure the
     *  stash stays, so a resume lands back on this gate rather than leaving the
     *  delegate holding paper for an account that quietly dies. */
    async function confirmCompletion(): Promise<void> {
        errorEl.classList.remove('cid-pane-error--visible');
        confirmBtn.setAttribute('disabled', '');
        retryBtn.setAttribute('disabled', '');
        retryBtn.style.display = 'none';
        input.disabled = true;
        const priorLabel = confirmBtn.textContent;
        confirmBtn.textContent = 'Confirming with the server…';
        try {
            await completeOnboardingThenWipe(
                [() => accountApi.completeOnboarding()],
                clearOnboardingStash,
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : 'unknown error';
            errorEl.textContent = `Could not confirm completion with the server — ${message}. Retry.`;
            errorEl.classList.add('cid-pane-error--visible');
            confirmBtn.style.display = 'none';
            confirmBtn.textContent = priorLabel;
            retryBtn.removeAttribute('disabled');
            retryBtn.style.display = '';
            return;
        }
        // The optional Login code step (§15.6): after the gate has passed and
        // the account is marked complete, never before — it must not be able to
        // block the completion it follows. The founding delegate and the
        // invited delegate both have an Authenticator Key; the ORGANIZATION
        // account has none (§15.1).
        const totpSecret = readStashedTotpSecret(shared);
        if (totpSecret) {
            gate.innerHTML = '';
            // See device-setup-restructure §6.5 — the checkbox is hidden when the
            // device step chose "No local storage".
            const loginCodeCid = String(shared['delegateCid'] ?? '');
            renderLoginCodeStep(gate, {
                cid: loginCodeCid,
                secret: totpSecret,
                onDone: renderDone,
                deviceToken: deviceStorage.getDeviceToken(loginCodeCid),
            });
            return;
        }
        renderDone();
    }

    function renderDone(): void {
        gate.innerHTML = '';
        gate.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Delegate Identity Activated' }));
        gate.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Your document set is confirmed. Store it safely. An organization administrator '
                + 'still has to provision your organization keys — until then you can sign in, but the '
                + 'organization\'s own resources stay closed.',
        }));
        const summary = createElement('div', { className: 'cid-wizard-summary' });
        summary.appendChild(createElement('p', {
            className: 'cid-wizard-text', textContent: `Catalog.ID: ${shared['delegateCid']}`,
        }));
        summary.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: 'Account type: Delegate' }));
        summary.appendChild(createElement('p', {
            className: 'cid-wizard-text', textContent: 'Status: Partial (awaiting organization key provisioning)',
        }));
        gate.appendChild(summary);

        const finishBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit',
            textContent: 'Finish',
        });
        finishBtn.addEventListener('click', () => ctx.wizard.next());
        gate.appendChild(finishBtn);
    }
}

/**
 * Refresh the stashed bitcash wallet key from the account's wallet document
 * before a print.
 *
 * §13.1's rule is "print what the account really has", and since
 * cid-encrypted-storage §7.2 what it really has is `cid/bitcash-wallets`, not a
 * key-bag entry. The stash — written by the finalize step from the document it
 * adopted or created — stays the fallback: a tab that cannot read the blob must
 * still print the key the voucher's bits were released into.
 */
async function refreshStashedWalletKey(shared: Record<string, unknown>): Promise<void> {
    try {
        const held = await registrationWalletKey();
        if (held) shared['bitcashWalletKey'] = held;
    } catch {
        // `registrationWalletKey` already swallows its own failures; this is
        // belt and braces, because nothing about a print may throw here.
    }
}

/** The wallet key the kit sheet prints, or null when the account has none. */
function stashedWalletKey(shared: Record<string, unknown>): string | null {
    const key = shared['bitcashWalletKey'];
    return typeof key === 'string' && key ? key : null;
}
